/** Whole-clip scenes: teal through blue and violet to magenta. */
export const SCENE_RAMP = { fromHue: 185, toHue: 340, saturation: 58, fromLightness: 56, toLightness: 60 };
/** Audio snippets: warm, chestnut through amber to gold, brightening as they go. */
export const AUDIO_RAMP = { fromHue: 12, toHue: 68, saturation: 52, fromLightness: 44, toLightness: 64 };
/** Video extras: a cool slate sweep, so a stand-out reads as its own kind of row. */
export const EXTRA_RAMP = { fromHue: 200, toHue: 250, saturation: 22, fromLightness: 52, toLightness: 62 };
/** Video highlights: violet through pink and red to orange. */
export const HIGHLIGHT_RAMP = { fromHue: 262, toHue: 400, saturation: 54, fromLightness: 58, toLightness: 60 };
/** The colour of row i of n on a ramp; a single row takes the ramp's start. */
export function rampColour(i, n, ramp) {
    const t = n <= 1 ? 0 : Math.min(1, Math.max(0, i / (n - 1)));
    const hue = Math.round((ramp.fromHue + (ramp.toHue - ramp.fromHue) * t) % 360);
    const light = Math.round(ramp.fromLightness + (ramp.toLightness - ramp.fromLightness) * t);
    return `hsl(${hue}, ${ramp.saturation}%, ${light}%)`;
}
/** #rrggbb to hue (0..360), saturation and lightness (0..1). */
export function hexToHsl(hex) {
    const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
    if (!m)
        return { h: 0, s: 0, l: 0 };
    const r = parseInt(m[1], 16) / 255;
    const g = parseInt(m[2], 16) / 255;
    const b = parseInt(m[3], 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    if (d === 0)
        return { h: 0, s: 0, l };
    const s = d / (1 - Math.abs(2 * l - 1));
    let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h = Math.round(h * 60);
    if (h < 0)
        h += 360;
    return { h, s, l };
}
/** A plain colour word for a swatch, the vocabulary a search can use ("five pink scenes in a row"). */
/** The Colour category's key: a clip's and each subclip's colours, as the analysis names them. */
export const COLOUR_FACET = 'colour';
/**
 * The Colour category is a small palette, the same for every brand (Dax, 2026-10-01): the basic colour
 * words people ask for in any language, each light or heavy, and the three neutrals. A clip's finer names
 * ("golden amber", "cream white") are kept as custom tags beside it, so a rare exact search still works.
 */
export const COLOUR_PALETTE_BASES = ['red', 'orange', 'yellow', 'green', 'blue', 'purple', 'pink', 'brown'];
export const COLOUR_SHADES = ['light', 'heavy'];
export const COLOUR_NEUTRALS = ['black', 'white', 'grey', 'cream'];
/** Every value a Colour category holds: "pink light", "pink heavy", …, "black", "white", "grey". */
export const COLOUR_PALETTE = [
    ...COLOUR_PALETTE_BASES.flatMap((base) => COLOUR_SHADES.map((shade) => `${base} ${shade}`)),
    ...COLOUR_NEUTRALS,
];
/** Words in a colour name that say how pale or strong it is. */
const SHADE_WORDS = {
    light: 'light', pale: 'light', pastel: 'light', soft: 'light', creamy: 'light', faded: 'light', muted: 'light', dusty: 'light',
    heavy: 'heavy', dark: 'heavy', deep: 'heavy', rich: 'heavy', bright: 'heavy', vivid: 'heavy', strong: 'heavy', bold: 'heavy', hot: 'heavy',
};
/** Colour nouns that are not palette words, as the palette colour (or neutral) and shade they read as. */
/** Words that make a name cream when no basic colour is named beside them (Dax, 2026-10-01). */
const CREAM_WORDS = ['cream', 'creamy', 'ivory', 'off-white', 'vanilla', 'eggshell'];
const COLOUR_NOUNS = {
    magenta: { base: 'pink', shade: 'heavy' }, fuchsia: { base: 'pink', shade: 'heavy' }, raspberry: { base: 'pink', shade: 'heavy' }, rose: { base: 'pink', shade: 'light' }, blush: { base: 'pink', shade: 'light' }, salmon: { base: 'pink', shade: 'light' },
    crimson: { base: 'red', shade: 'heavy' }, scarlet: { base: 'red', shade: 'heavy' }, maroon: { base: 'red', shade: 'heavy' }, burgundy: { base: 'red', shade: 'heavy' }, cherry: { base: 'red', shade: 'heavy' },
    peach: { base: 'orange', shade: 'light' }, apricot: { base: 'orange', shade: 'light' }, coral: { base: 'orange', shade: 'light' }, amber: { base: 'orange', shade: 'heavy' }, copper: { base: 'orange', shade: 'heavy' }, rust: { base: 'orange', shade: 'heavy' }, terracotta: { base: 'orange', shade: 'heavy' },
    lemon: { base: 'yellow', shade: 'light' }, butter: { base: 'yellow', shade: 'light' }, blonde: { base: 'yellow', shade: 'light' }, gold: { base: 'yellow', shade: 'heavy' }, golden: { base: 'yellow', shade: 'heavy' }, mustard: { base: 'yellow', shade: 'heavy' },
    mint: { base: 'green', shade: 'light' }, sage: { base: 'green', shade: 'light' }, lime: { base: 'green', shade: 'heavy' }, olive: { base: 'green', shade: 'heavy' }, emerald: { base: 'green', shade: 'heavy' },
    teal: { base: 'blue', shade: 'heavy' }, turquoise: { base: 'blue', shade: 'light' }, aqua: { base: 'blue', shade: 'light' }, navy: { base: 'blue', shade: 'heavy' }, denim: { base: 'blue', shade: 'heavy' }, indigo: { base: 'blue', shade: 'heavy' },
    violet: { base: 'purple', shade: 'heavy' }, lilac: { base: 'purple', shade: 'light' }, lavender: { base: 'purple', shade: 'light' }, plum: { base: 'purple', shade: 'heavy' },
    beige: { base: 'brown', shade: 'light' }, tan: { base: 'brown', shade: 'light' }, caramel: { base: 'brown', shade: 'light' }, taupe: { base: 'brown', shade: 'light' }, chocolate: { base: 'brown', shade: 'heavy' }, coffee: { base: 'brown', shade: 'heavy' }, mocha: { base: 'brown', shade: 'heavy' },
    silver: { base: 'grey' }, charcoal: { base: 'grey' }, gray: { base: 'grey' },
};
/**
 * Any colour name as the palette value it belongs to, or null for one that is not a colour ("skin tone",
 * "clear"). The palette word in the name decides the colour ("brown caramel" is brown, "yellow creamy" is
 * yellow); without one a cream word makes it cream ("cream white", "off-white", "beige creamy"); else the
 * colour noun decides ("golden amber" is orange, its last). The shade comes from a shade word, else the
 * noun's own, else heavy, the colour at full strength. Neutrals take no shade. Deterministic, so a migration and the worker
 * agree on every name.
 */
export function paletteColour(name) {
    const clean = name.trim().toLowerCase().replace(/\bgray\b/g, 'grey');
    if (COLOUR_PALETTE.includes(clean))
        return clean;
    const words = clean.split(/[\s,]+/).flatMap((w) => (COLOUR_NOUNS[w] || CREAM_WORDS.includes(w) ? [w] : w.split('-'))).filter(Boolean);
    const isBase = (w) => COLOUR_PALETTE_BASES.includes(w);
    const isNeutral = (w) => COLOUR_NEUTRALS.includes(w);
    if (!words.some(isBase) && words.some((w) => CREAM_WORDS.includes(w)))
        return 'cream';
    const nouns = words.filter((w) => COLOUR_NOUNS[w]).map((w) => COLOUR_NOUNS[w]);
    // The colour named last carries the name: "red-pink" is a pink, "golden amber" an amber.
    const named = [...words].reverse().find((w) => isBase(w) || isNeutral(w));
    const base = named ?? nouns[nouns.length - 1]?.base;
    if (!base)
        return null;
    if (isNeutral(base))
        return base;
    const shade = words.map((w) => SHADE_WORDS[w]).find(Boolean) ?? nouns.find((n) => n.shade)?.shade ?? 'heavy';
    return `${base} ${shade}`;
}
/** Colours a shade can belong to: a name ending in one reads colour first (migration 038 holds the same list). */
export const COLOUR_BASES = ['red', 'pink', 'magenta', 'peach', 'orange', 'yellow', 'gold', 'cream', 'beige', 'green', 'teal', 'blue', 'purple', 'brown', 'black', 'grey', 'silver', 'white'];
/** Names close enough to one colour to be that colour. */
const COLOUR_MERGES = { 'chocolate brown': 'brown' };
/**
 * A colour as the Colour category spells it: the colour first, then its shade, so every shade of a colour
 * sits together in an alphabetical list ("light brown" is "brown light", "mint green" is "green mint").
 * Lower case, single spaces, grey spelt grey. A name already colour first, or not ending in a colour
 * ("skin tone"), keeps its words. (Dax, 2026-09-30)
 */
export function colourLabel(name) {
    const clean = name.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\bgray\b/g, 'grey');
    const merged = COLOUR_MERGES[clean] ?? clean;
    const words = merged.split(' ');
    const last = words[words.length - 1];
    if (words.length < 2 || !COLOUR_BASES.includes(last) || COLOUR_BASES.includes(words[0]))
        return merged;
    return [last, ...words.slice(0, -1)].join(' ');
}
/** Light or heavy for a chromatic swatch: pale where it is light or washed out, heavy at full strength. */
function shadeOf(s, l) {
    return l >= 0.7 || (s < 0.35 && l >= 0.5) ? 'light' : 'heavy';
}
/** A swatch as its palette value (COLOUR_PALETTE), the vocabulary a search uses ("five pink scenes in a row"). */
export function nameColour(hex) {
    const { h, s, l } = hexToHsl(hex);
    if (l < 0.12)
        return 'black';
    if (l > 0.8 && s >= 0.2 && s < 0.6 && h >= 20 && h <= 70)
        return 'cream';
    if (l > 0.92)
        return 'white';
    if (s < 0.12)
        return l > 0.85 ? 'white' : 'grey';
    if (h < 15 || h >= 345)
        return l > 0.7 ? 'pink light' : `red ${shadeOf(s, l)}`;
    if (h < 50 && l < 0.4)
        return `brown ${l >= 0.28 ? 'light' : 'heavy'}`;
    if (h < 40)
        return `orange ${shadeOf(s, l)}`;
    if (h < 65)
        return `yellow ${shadeOf(s, l)}`;
    if (h < 160)
        return `green ${shadeOf(s, l)}`;
    if (h < 250)
        return `blue ${shadeOf(s, l)}`;
    if (h < 290)
        return `purple ${shadeOf(s, l)}`;
    return `pink ${l > 0.65 ? 'light' : 'heavy'}`;
}
//# sourceMappingURL=colour.js.map